// @ts-nocheck
import React, { useMemo, useCallback } from 'react';
import { Grid, TextField, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import {
  checkAttributeIsRequired,
  getErrorHelperText,
  normalizeParagraphValue,
} from '../../../../utils/Utils';
import { EMPTY_OBJECT } from '../../../../const/CommonConst';

/**
 * Text field component for editing paragraph attributes (title & description)
 * @param {Object} props - Component props
 * @param {Object|string} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - Attribute metadata
 * @param {Object} props.entityDetailsFormikForm - Formik instance
 * @param {string} props.label - Field label
 * @returns {React.ReactElement} The ParagraphFieldRenderer component
 */
const ParagraphFieldRenderer = ({
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  label,
}) => {
  const isAttributeMandatory = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);

  const resolvedValue = useMemo(() => {
    if (value !== undefined && value !== null) return value;
    const attrId = attribute?.id;
    if (attrId != null && entityDetailsFormikForm?.values) {
      return (
        entityDetailsFormikForm.values[attrId] ??
        entityDetailsFormikForm.values[Number(attrId)] ??
        entityDetailsFormikForm.values[String(attrId)]
      );
    }
    return value;
  }, [value, attribute?.id, entityDetailsFormikForm?.values]);

  const paragraphValue = useMemo(
    () => normalizeParagraphValue(resolvedValue),
    [resolvedValue]
  );

  const handleTitleChange = useCallback(
    (e) => {
      const newTitle = e.target.value;
      const nextValue = {
        title: newTitle,
        description: paragraphValue.description,
      };
      onChange?.(nextValue);
      if (entityDetailsFormikForm?.setFieldValue && attribute?.id != null) {
        entityDetailsFormikForm.setFieldValue(attribute.id, nextValue);
      }
    },
    [
      paragraphValue.description,
      onChange,
      entityDetailsFormikForm?.setFieldValue,
      attribute?.id,
    ]
  );

  const handleDescriptionChange = useCallback(
    (e) => {
      const newDescription = e.target.value;
      const nextValue = {
        title: paragraphValue.title,
        description: newDescription,
      };
      onChange?.(nextValue);
      if (entityDetailsFormikForm?.setFieldValue && attribute?.id != null) {
        entityDetailsFormikForm.setFieldValue(attribute.id, nextValue);
      }
    },
    [
      paragraphValue.title,
      onChange,
      entityDetailsFormikForm?.setFieldValue,
      attribute?.id,
    ]
  );

  const handleTitleBlur = useCallback(
    (e) => {
      entityDetailsFormikForm?.handleBlur?.(e);
      if (attribute?.id != null) {
        entityDetailsFormikForm?.setFieldTouched?.(
          `${attribute.id}.title`,
          true
        );
      }
    },
    [entityDetailsFormikForm?.setFieldTouched, attribute?.id]
  );

  const handleDescriptionBlur = useCallback(
    (e) => {
      entityDetailsFormikForm?.handleBlur?.(e);
      if (attribute?.id != null) {
        entityDetailsFormikForm?.setFieldTouched?.(
          `${attribute.id}.description`,
          true
        );
      }
    },
    [entityDetailsFormikForm?.setFieldTouched, attribute?.id]
  );

  const errors = entityDetailsFormikForm?.errors;
  const touched = entityDetailsFormikForm?.touched;
  const attrErrors =
    (attribute?.id != null
      ? (errors?.[attribute.id] ?? errors?.[String(attribute.id)])
      : null) ?? EMPTY_OBJECT;
  const attrTouched =
    (attribute?.id != null
      ? (touched?.[attribute.id] ?? touched?.[String(attribute.id)])
      : null) ?? EMPTY_OBJECT;

  const titleError =
    typeof attrErrors === 'string' ? attrErrors : (attrErrors?.title ?? '');
  const titleTouched =
    typeof attrTouched === 'boolean'
      ? attrTouched
      : Boolean(attrTouched?.title);

  const descError =
    typeof attrErrors === 'string'
      ? attrErrors
      : (attrErrors?.description ?? '');
  const descTouched =
    typeof attrTouched === 'boolean'
      ? attrTouched
      : Boolean(attrTouched?.description);

  const titleHelperText = getErrorHelperText(titleError, titleTouched);
  const descHelperText = getErrorHelperText(descError, descTouched);

  return (
    <Grid
      container
      size={12}
      sx={{
        gap: 2,
      }}
    >
      <Grid size={12}>
        <TextField
          required={isAttributeMandatory}
          label={label}
          name={attribute?.id != null ? `${attribute.id}.title` : undefined}
          fullWidth
          value={paragraphValue.title}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          placeholder="Enter a title"
          spellCheck
          type="text"
          slotProps={{
            htmlInput: {
              title: paragraphValue.title || '',
            },
          }}
          error={Boolean(titleHelperText)}
          helperText={titleHelperText}
          disabled={!attribute?.is_editable}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          fullWidth
          required={isAttributeMandatory}
          name={
            attribute?.id != null ? `${attribute.id}.description` : undefined
          }
          value={paragraphValue.description}
          onChange={handleDescriptionChange}
          onBlur={handleDescriptionBlur}
          label={
            <Tooltip title={attribute?.justification_label}>
              <Typography
                variant="body4"
                sx={{
                  display: 'inline-block',
                  maxWidth: '340px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'middle',
                }}
              >
                {attribute?.justification_label}
              </Typography>
            </Tooltip>
          }
          placeholder="Enter a description"
          multiline
          minRows={4}
          spellCheck
          error={Boolean(descHelperText)}
          helperText={descHelperText}
          disabled={!attribute?.is_editable}
          slotProps={{
            htmlInput: { title: paragraphValue.description || '' },
          }}
        />
      </Grid>
    </Grid>
  );
};

ParagraphFieldRenderer.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  attribute: PropTypes.shape({
    is_editable: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    justification_label: PropTypes.string,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    handleBlur: PropTypes.func,
    errors: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    touched: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
    values: PropTypes.shape({}),
  }),
  label: PropTypes.string.isRequired,
};

export default ParagraphFieldRenderer;
